import type { RouteObject } from "react-router-dom";
import { matchRoutes } from "react-router-dom";

import HomePage from "../pages/HomePage";
import TemplateListPage from "../pages/Main/TemplateListPage";
import TemplateCreatePage from "../pages/Main/TemplateCreatePage";
import FormCreatePage from "../pages/FormBuilder/FormCreatePage";
import FormViewPage from "../pages/FormBuilder/FormViewPage";
import CreateRequestPage from "../pages/Requests/CreateRequestPage";
import ListPage from "../pages/Requests/ListPage";
import ViewRequestPage from "../pages/Requests/ViewRequestPage";
import EditRequestPage from "../pages/Requests/EditRequestPage";
import MyApprovalsPage from "../pages/Approvals/MyApprovalsPage";
import DashboardPage from "../pages/Dashboard/DashboardPage";
import RequestListPage from "../pages/Requests/RequestListPage";
import MyRequestsListPage from "../pages/Requests/MyRequestsListPage";
import WorksPlanPage from "../pages/WorksPlan/WorksPlanPage";
import ProcessTemplatesPage from "../pages/Process/ProcessTemplatesPage";
import ProcessDesignerPage from "../pages/Process/ProcessDesignerPage";
import FormDesignerPage from "../pages/FormDesigner/FormDesignerPage";
import TemplateSetupPage from "../pages/Templates/TemplateSetupPage";
import ReportsListPage from "../pages/Reports/ReportsListPage";
import ReportBuilderPage from "../pages/Reports/ReportBuilderPage";
import ReportViewPage from "../pages/Reports/ReportViewPage";
import OrganisationPage from "../pages/Organisation/OrganisationPage";
import DelegationsPage from "../pages/Delegations/DelegationsPage";

/**
 * The pages that render inside the tabbed workspace. `useRoutes(routeObjects,
 * tabPath)` resolves each open tab to one of these, keeping its own params.
 */
export const routeObjects: RouteObject[] = [
  { path: "/", element: <HomePage /> },
  { path: "/templates", element: <TemplateListPage /> },
  { path: "/templates/create", element: <TemplateCreatePage /> },
  // Building a template out: details, sections, process, publish.
  { path: "/templates/:shortName/setup", element: <TemplateSetupPage /> },
  // The process designer: which templates I administer, then one template's
  // stages, section ownership and approval slots.
  { path: "/process", element: <ProcessTemplatesPage /> },
  { path: "/templates/:id/process", element: <ProcessDesignerPage /> },
  { path: "/forms/create", element: <FormCreatePage /> },
  { path: "/forms/view", element: <FormViewPage /> },
  // The in-app form designer on antd 5, replacing the standalone antd 4 app.
  { path: "/forms/:id/design", element: <FormDesignerPage /> },
  { path: "/my-requests", element: <MyRequestsListPage /> },
  { path: "/my-approvals", element: <MyApprovalsPage /> },
  // Cover while somebody is away (D2). Not an administrative screen: arranging
  // your own leave is not an administrative act, so it sits beside My Actions
  // rather than under Organisation.
  { path: "/delegations", element: <DelegationsPage /> },
  { path: "/analytics", element: <DashboardPage /> },
  // Saved reports: pick a template, pick its fields as columns, run over a
  // period, export. "/reports/new" outranks "/reports/:id" by specificity.
  { path: "/reports", element: <ReportsListPage /> },
  { path: "/reports/new", element: <ReportBuilderPage /> },
  { path: "/reports/:id", element: <ReportViewPage /> },
  { path: "/reports/:id/edit", element: <ReportBuilderPage /> },
  // People, groups, the org chart and the directory connection. One route with
  // a ?tab=, not four: adding somebody means putting them in a unit, naming
  // their manager and dropping them into a group, and that is one job.
  { path: "/organisation", element: <OrganisationPage /> },
  { path: "/works-plan", element: <WorksPlanPage /> },
  { path: "/requests/list", element: <ListPage /> },
  { path: "/requests/edit/:id", element: <EditRequestPage /> },
  { path: "/requests/:shortName/create", element: <CreateRequestPage /> },
  { path: "/requests/:typeName/:shortName", element: <RequestListPage /> },
  { path: "/requests/:id", element: <ViewRequestPage /> },
  {
    path: "*",
    element: <div style={{ padding: 24 }}>Page not found</div>,
  },
];

// Default tab titles per route pattern. Dynamic pages refine these at runtime
// via useTabTitle().
const TITLE_BY_PATTERN: Record<string, string> = {
  "/": "Home",
  "/templates": "Templates",
  "/templates/create": "New template",
  "/templates/:shortName/setup": "Template setup",
  "/process": "Process",
  "/templates/:id/process": "Process",
  "/forms/create": "Create Form",
  "/forms/view": "Form",
  "/forms/:id/design": "Form Designer",
  "/my-requests": "My Requests",
  "/my-approvals": "My Actions",
  "/delegations": "Cover",
  "/analytics": "Analytics",
  "/reports": "Reports",
  "/reports/new": "New report",
  "/reports/:id": "Report",
  "/reports/:id/edit": "Edit report",
  "/organisation": "Organisation",
  "/works-plan": "Works Plan",
  "/requests/list": "Requests",
  "/requests/edit/:id": "Edit Request",
  "/requests/:shortName/create": "Create Request",
  "/requests/:typeName/:shortName": "Requests",
  "/requests/:id": "Request",
};

/** Derives a sensible default title for a given URL path. */
export function deriveTabTitle(pathname: string): string {
  const matches = matchRoutes(routeObjects, pathname);
  const leaf = matches?.[matches.length - 1];
  const pattern = leaf?.route.path;
  return (pattern && TITLE_BY_PATTERN[pattern]) || "Page";
}
